
import { useState } from 'react';
import { type Gift, type Brand, type MarketAction } from '@/utils/types';
import { toast } from '@/hooks/use-toast';
import { useInventorySelection } from './useInventorySelection';
import { useInventoryNavigation } from './useInventoryNavigation';
import { useMarketActions } from './useMarketActions';

export function useInventoryActions(initialGifts: Gift[]) {
  const {
    selectedGift,
    targetGift,
    targetEmail,
    gifts,
    currentBrand,
    setSelectedGift,
    setGifts,
    setTargetGift,
    setTargetEmail,
    setCurrentBrand,
    handleGiftSelect,
  } = useInventorySelection(initialGifts);
  
  const {
    activeTab,
    viewingTransactions,
    showQRCode,
    setViewingTransactions,
    handleTabChange: navigationTabChange,
    viewBrandTransactions,
    handleBackFromTransactions,
    handleShowQRCode,
    handleCancelQRCode,
    setShowQRCode,
  } = useInventoryNavigation();
  
  // Create a mock transactions array since we need it for useMarketActions
  const [transactions, setTransactions] = useState<any[]>([]);
  
  const {
    isProcessing,
    handleConsume,
    handleExchange,
    handleRegift,
    handleConvert
  } = useMarketActions({
    gifts,
    brands: [], // We can pass an empty array as it's not used in the specific functions we need
    setGifts,
    transactions,
    setTransactions,
    selectedGift,
    setSelectedGift
  });
  
  const handleTabChange = (value: MarketAction) => {
    navigationTabChange(value);
  };
  
  const handleMarketAction = () => {
    console.log("handleMarketAction called with tab:", activeTab);
    console.log("Current state:", { activeTab, selectedGift, targetEmail, targetGift });
    
    if (!selectedGift) {
      toast({
        title: "No gift selected",
        description: "Please select a GivGo Bucks first",
        variant: "destructive"
      });
      return;
    }

    if (activeTab === 'consume') {
      console.log("QR CODE TRIGGER: Via handleMarketAction");
      handleShowQRCode();
      handleConsume(selectedGift);
      return;
    }
    
    if (activeTab === 'regift') {
      console.log("Processing regift action with email:", targetEmail);
      if (!targetEmail) {
        toast({
          title: "Missing recipient",
          description: "Please enter an email address or select a contact",
          variant: "destructive"
        });
        return;
      }
      
      handleRegift(selectedGift, targetEmail);
      return;
    }

    if (activeTab === 'exchange') {
      if (!targetGift) {
        toast({
          title: "Exchange error",
          description: "Please select a gift to exchange for",
          variant: "destructive"
        });
        return;
      }
      
      handleExchange(selectedGift, targetGift.id);
      return;
    }
    
    if (activeTab === 'convert') {
      // For convert, we use the gift amount as the conversion amount
      handleConvert(selectedGift, selectedGift.amount);
      return;
    }
  };
  
  const handleMarkGiftAsUsed = () => {
    if (selectedGift) {
      // Use handleConsume directly
      handleConsume(selectedGift);
      setShowQRCode(false);
      setSelectedGift(null);
    }
  };
  
  const handleTransactionAction = (action: MarketAction, selectedGifts: Gift[]) => {
    // Process each gift based on the action
    if (selectedGifts.length > 0) {
      const gift = selectedGifts[0]; // For now, just handle the first gift
      
      if (action === 'consume') {
        handleConsume(gift);
      } else if (action === 'exchange' && targetGift) {
        handleExchange(gift, targetGift.id);
      } else if (action === 'regift' && targetEmail) {
        handleRegift(gift, targetEmail);
      } else if (action === 'convert') {
        handleConvert(gift, gift.amount);
      }
    }
    
    setViewingTransactions(false);
    setCurrentBrand(null);
  };
  
  return {
    activeTab,
    selectedGift,
    targetGift,
    targetEmail,
    isProcessing,
    gifts,
    currentBrand,
    viewingTransactions,
    showQRCode,
    handleTabChange,
    handleGiftSelect,
    setTargetGift,
    setTargetEmail,
    viewBrandTransactions,
    handleBackFromTransactions,
    handleShowQRCode,
    handleCancelQRCode,
    handleMarkGiftAsUsed,
    handleTransactionAction,
    handleMarketAction
  };
}
